import React, { useEffect, useRef, useState } from "react";
import Button from "../Button";
import { postSignatureImage } from "@api/index";
import { styled } from "@linaria/react";
import {
  SignatureProps,
  updateSignature,
} from "@redux/slices/SHIELD_STARTER/step3Slice";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { setLoadingState } from "@redux/slices/loadingSlice";
import { Text } from "../Text";
import { SCREEN_SIZE } from "@static/screen-size";

interface SingnatureCanvasProps {
  showSignModal: (state: boolean) => void;
  consentType: String;
}

export default function SingnatureCanvas({
  showSignModal,
  consentType,
}: SingnatureCanvasProps) {
  const dispatch = useAvivaDispatch();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [clearSignature, setClearSignature] = useState(false);
  const transactionId = useAvivaSelector(
    (state) => state.shieldStarterStep3.transactionId
  );
  const [updateOnReSize, setUpdateOnResize] = useState(1);
  const image = useRef("");
  const [canvasContext, setCanvaContext] = useState<any>(null);

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    const htmlElement = document.querySelector("html");
    if (htmlElement) {
      htmlElement.style.overflowY = "hidden";
    }
    let isDrawing = false;
    const canvas = canvasRef.current!;
    // canvas.style.width = "inherit";
    // canvas.style.height = "inherit";
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    let pointerPosition = { x: 0, y: 0 };
    let prevPointerPosition = pointerPosition;

    if (canvasContext === null) {
      setCanvaContext(canvasRef.current?.getContext("2d"));
      return;
    }

    //Resize alignment
    function sizeUpdater() {
      setUpdateOnResize((s) => s + 1);
    }
    window.addEventListener("resize", sizeUpdater);

    //draw animation
    function drawingLoop() {
      requestAnimationFrame(drawingLoop);
      renderCanvas();
    }

    //To get the position to start painting
    function getMousePointerPosition(e: MouseEvent) {
      const canvasBounding = canvas.getBoundingClientRect();
      return {
        x: e?.clientX - canvasBounding.left,
        y: e?.clientY - canvasBounding.top,
      };
    }
    function getTouchPointerPosition(e: TouchEvent) {
      const canvasBounding = canvas.getBoundingClientRect();
      return {
        x: e.touches[0]?.clientX - canvasBounding.left,
        y: e.touches[0]?.clientY - canvasBounding.top,
      };
    }

    //When user initiates the sign with mouse or touch
    function mouseDown(e: MouseEvent) {
      isDrawing = true;
      prevPointerPosition = getMousePointerPosition(e);
    }
    function touchDown(e: TouchEvent) {
      prevPointerPosition = getTouchPointerPosition(e);
    }

    //When user releases the touch or mouse button to stop the sign
    function mouseUp(e: MouseEvent) {
      isDrawing = false;
      pointerPosition = getMousePointerPosition(e);
    }
    function touchUp(e: TouchEvent) {
      isDrawing = false;
      pointerPosition = getTouchPointerPosition(e);
    }
    function mouseClick(e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      isDrawing = true;
      pointerPosition = getMousePointerPosition(e);
      prevPointerPosition = {
        x: pointerPosition.x + 1,
        y: pointerPosition.y + 1,
      };
      renderCanvas();
      isDrawing = false;
    }

    //When user moves the mouse or drags the touch
    function mouseMove(e: MouseEvent) {
      pointerPosition = getMousePointerPosition(e);
    }
    function touchMove(e: TouchEvent) {
      isDrawing = true;
      pointerPosition = getTouchPointerPosition(e);
    }
    function renderCanvas() {
      if (isDrawing) {
        setClearSignature(true);
        canvasContext.strokeStyle = "black";
        canvasContext.lineWidth = 5;
        canvasContext.moveTo(prevPointerPosition.x, prevPointerPosition.y);
        canvasContext.lineTo(pointerPosition.x, pointerPosition.y);
        canvasContext.stroke();
        prevPointerPosition = pointerPosition;
      }
    }
    if (image.current) {
      const image64 = new Image();
      image64.src = image.current;
      image64.onload = () => {
        canvasContext.drawImage(image64, 0, 0);
      };
    }
    if (canvas) {
      // canvasContext = canvas.getContext("2d")!;
      // canvasContext.strokeStyle = "#0000";
      // canvasContext.lineWidth = 4;

      canvas.addEventListener("mousedown", mouseDown);
      canvas.addEventListener("touchstart", touchDown);
      canvas.addEventListener("touchmove", touchMove);
      canvas.addEventListener("mousemove", mouseMove);
      canvas.addEventListener("touchend", touchUp);
      canvas.addEventListener("mouseup", mouseUp);
      canvas.addEventListener("click", mouseClick);
      drawingLoop();
    }

    return () => {
      isDrawing = false;
      canvas.removeEventListener("mousedown", mouseDown);
      canvas.removeEventListener("touchstart", touchDown);
      canvas.removeEventListener("touchmove", touchMove);
      canvas.removeEventListener("mousemove", mouseMove);
      canvas.removeEventListener("touchend", touchUp);
      canvas.removeEventListener("mouseup", mouseUp);
      canvas.removeEventListener("click", mouseClick);
      window.removeEventListener("resize", sizeUpdater);
      if (htmlElement) {
        htmlElement.style.overflowY = "unset";
      }
      document.body.style.overflowY = "unset";
      image.current = canvasRef.current?.toDataURL()!;
    };
  }, [updateOnReSize, canvasContext]);

  async function onSubmit() {
    try {
      dispatch(setLoadingState(true));
      const base64SignatureImage = canvasRef.current?.toDataURL().split(",")[1];
      const payload = {
        base64SignatureImage,
        transactionId: transactionId,
        documentType: "signature",
        fileType: "image/png",
      };
      const { data } = await postSignatureImage(JSON.stringify(payload));
      dispatch(updateSignature(data));
      dispatch(setLoadingState(false));
    } catch (error) {
      dispatch(setLoadingState(false));
    }

    showSignModal(false);
  }

  //Clear the canvas to remove the existing sign
  function clearCanvas() {
    setClearSignature(false);
    const canvas = canvasRef.current!;
    canvas.width = canvas.width!;
  }

  return (
    <div>
      <div>
        <Text variant="title" fontSize="18px" margin="0 0 16px 0">
          {consentType}'s Signature
        </Text>

        <TextContainer>
          <Text variant="base" fontSize="14px" margin="0 0 16px 0">
            Click the box below to sign:
          </Text>
          {clearSignature && (
            <TextStyle onClick={clearCanvas}>Clear Signature</TextStyle>
          )}
        </TextContainer>

        <Canvas
          style={{
            background: "white",
            border: "solid #D8D8D8 1px",
            borderRadius: "8px",
          }}
          ref={canvasRef}
          // width={600}
          // height={300}
        >
          Where is the canvas
        </Canvas>
      </div>

      <ButtonContainer>
        <Button
          variant="secondary"
          onClick={() => showSignModal(false)}
          label="Cancel"
          width={"180px"}
        />
        <Button
          onClick={onSubmit}
          label="Confirm"
          width={"180px"}
          disabled={!clearSignature}
        />
      </ButtonContainer>
    </div>
  );
}

const Canvas = styled.canvas`
  height: 35vh;
  width: 100%;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    height: 380px;
    width: 597px;
  }
`;

const TextStyle = styled.div`
  color: #883E89;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 10px;
  flex-direction: row;
  justify-content: center;
  margin-top: 24px;

  @media screen and (min-width: 768px) {
    margin-top: 24px;
    column-gap: 30px;
    flex-direction: row;
    justify-content: center;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 10px;
  flex-direction: row;
  justify-content: space-between;

  @media screen and (min-width: 768px) {
    flex-direction: column;
    column-gap: 30px;
    flex-direction: row;
    justify-content: space-between;
  }
`;
